/**
 * DisclosurePanel — what the Driver is told BEFORE the hire purchase agreement opens.
 *
 * ── WHY THIS SCREEN EXISTS, AND WHY IT IS FIRST ───────────────────────────────
 * The Schedule has the Driver state, under "What the Driver states to be true", that
 *
 *   · "the Cash price and the Hire-purchase price shown above were stated to the Driver,
 *      orally and in writing and OTHERWISE THAN IN THIS AGREEMENT, BEFORE THIS AGREEMENT WAS
 *      MADE" — the pre-contract disclosure the Hire-Purchase Act, 1974 (NRCD 292) s.2 requires;
 *   · "the Driver was told before signing that the Vehicle is a second-hand vehicle, that ANY
 *      TERM ABOUT ITS QUALITY OR CONDITION WHICH THE LAW WOULD OTHERWISE IMPLY IS EXCLUDED,
 *      AND WHAT THAT EXCLUSION MEANS" — which is what clause 22.3 of the Terms says Primelane
 *      does "before the Agreement is made".
 *
 * Both are statements about something that happened OUTSIDE the instrument and BEFORE it. A
 * panel folded into the intro step, or shown after the reader, would be inside the ceremony's
 * account of the agreement and after the text; this is the first screen a signer sees, ahead
 * of the plain-English key terms and well ahead of the document. The ordering is the
 * substance, not the styling.
 *
 * ── THE STATUTE IS THIS FILE'S REASON, NEVER THE DRIVER'S READING ─────────────
 * The v3 pass took every Hire-Purchase Act citation out of the published bodies and v4 keeps
 * them out: the documents volunteer no statutory signpost, and a ceremony screen a driver
 * reads is the same surface. The Act is named in THESE COMMENTS because it is why this step
 * exists and why it is first — nothing below the render names it, and nothing should. The
 * panel says what the agreement says, in the agreement's own words.
 *
 * ── NO TICK, NO TIMER ─────────────────────────────────────────────────────────
 * The statement is made true by being MADE, and the Driver attests to it inside the Schedule
 * they then sign. A confirmation box here would collect a second, weaker attestation to the
 * same fact and add friction nobody asked for. One Continue.
 *
 * That is also why rewriting this copy does not move `CONSENT_TEXT_VERSION`. The constant
 * records the wording a SIGNATURE was taken against, and nothing on this screen is ticked;
 * the hire purchase checklist in `agreement-key-terms.ts` names neither quality nor repairs,
 * so neither the v4 rewrite here (the warranty out, the statute out) nor the §31 trim (the
 * written-agreement exception out) moved the constant. Every bump since has been about
 * sentences that ARE ticked. The value is not quoted here on purpose: it lives in `esign.ts`
 * and a copy of it in this comment is a line that rots one release later.
 *
 * ── THE DEGRADED FORM STATES NO FIGURE ────────────────────────────────────────
 * `contracts.cash_price` is staging-only until fleet-backend `20260816010000` promotes, so
 * this panel has to read without it. It then says the two prices were stated, and names
 * neither. It NEVER fills the gap from `sold_at`: that would print the Cash price and the
 * Hire-purchase price as the same number, which states on a disclosure screen that this
 * contract carries no charge for credit.
 */

import type { ReactNode } from 'react';

import { Eyebrow, StepHeading } from '@/components/agreements/CeremonyPieces';

interface Props {
    /** `contracts.cash_price`. Null where the column, the row or the read is unavailable. */
    cashPrice: number | null;
    /** `contract.sold_at` — the Schedule's Hire-purchase price. */
    hirePurchasePrice: number | null;
    /**
     * The deposit already credited, for the row the money grid calls "Deposit credited" —
     * null on a contract that has taken none, which is what the grid's own resolver answers
     * there. Callers derive it through `scheduleDepositCredited` in `agreement-key-terms.ts`,
     * whose note says why the recorded payment COUNT is the guard and the figure is not: the
     * payload answers a deposit-less contract with zeros, and a zero here would print
     * "Deposit credited GHS 0.00" under a row the Schedule omits entirely.
     */
    depositCredited: number | null;
    /**
     * The row the money grid calls "Weekly Instalment", which is the SCHEDULE'S figure:
     * `contract_instalments.amount`, latest by `starts_at` — never the insights payload's
     * `weekly_rate`, which adds every advance's instalment on top. See
     * `scheduleWeeklyInstalment` in `agreement-key-terms.ts` for why the two differ.
     */
    weeklyInstalment: number | null;
}

const currency = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
});

/** Rounding happens here and only here, as it does everywhere money is shown. */
function money(amount: number | null | undefined): string | null {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) return null;
    return currency.format(amount);
}

/**
 * One row of the money grid. The LABELS ARE THE SCHEDULE'S OWN, verbatim — a driver who
 * reads "Vehicle price" here and "Hire-purchase price" two screens later has been shown two
 * figures, not one figure twice.
 */
function PriceRow({
    label,
    value,
    note,
    lead,
}: {
    label: string;
    value: string;
    note?: string;
    lead?: boolean;
}) {
    return (
        <div className="flex items-baseline justify-between gap-4 border-b border-border py-3 last:border-b-0">
            <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">{label}</p>
                {note && (
                    <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                        {note}
                    </p>
                )}
            </div>
            <p
                className={
                    lead
                        ? 'shrink-0 text-[16px] font-bold tabular-nums tracking-tight text-foreground'
                        : 'shrink-0 text-[13.5px] font-semibold tabular-nums text-foreground'
                }
            >
                {value}
            </p>
        </div>
    );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="rounded-xl border border-border p-4">
            <p className="text-[13px] font-bold tracking-tight text-foreground">{title}</p>
            <div className="mt-1.5 space-y-2 text-[12.5px] leading-relaxed text-muted-foreground">
                {children}
            </div>
        </div>
    );
}

export default function DisclosurePanel({
    cashPrice,
    hirePurchasePrice,
    depositCredited,
    weeklyInstalment,
}: Props) {
    const cash = money(cashPrice);
    const hirePurchase = money(hirePurchasePrice);

    // THE CHARGE FOR CREDIT IS ONLY STATED WHEN IT IS A CHARGE. Both figures have to have
    // resolved, and the second has to exceed the first; a difference of zero or less is a
    // data problem, and printing "the charge for credit is -GHS 400" on the screen whose one
    // job is to disclose that charge would be worse than saying nothing about it.
    const creditCharge =
        cashPrice !== null &&
        hirePurchasePrice !== null &&
        Number.isFinite(cashPrice) &&
        Number.isFinite(hirePurchasePrice) &&
        hirePurchasePrice > cashPrice
            ? money(hirePurchasePrice - cashPrice)
            : null;

    const deposit = money(depositCredited);
    const weekly = money(weeklyInstalment);

    return (
        <>
            <div>
                <Eyebrow>Before the agreement</Eyebrow>
                <StepHeading>What this vehicle costs</StepHeading>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                    Primelane is stating these to you now, before the agreement is put in
                    front of you.
                </p>
            </div>

            {cash && hirePurchase ? (
                <div className="rounded-xl border border-border px-4 py-1">
                    <PriceRow
                        label="Cash price"
                        value={cash}
                        note="What the vehicle could be bought outright for"
                        lead
                    />
                    <PriceRow
                        label="Hire-purchase price"
                        value={hirePurchase}
                        note="The total payable to complete the purchase"
                        lead
                    />
                    {deposit && <PriceRow label="Deposit credited" value={deposit} />}
                    {weekly && <PriceRow label="Weekly Instalment" value={weekly} />}
                </div>
            ) : (
                <Panel title="The two prices">
                    <p>
                        The cash price and the hire-purchase price were stated to you before
                        this agreement was prepared.
                        {hirePurchase
                            ? ` The hire-purchase price is ${hirePurchase} — the total payable to complete the purchase.`
                            : ''}
                    </p>
                    <p>
                        If you have not been told both figures, contact Primelane before you
                        sign.
                    </p>
                </Panel>
            )}

            {creditCharge && (
                <Panel title="The difference is the charge for credit">
                    <p>
                        Taking the vehicle on hire purchase rather than paying cash costs{' '}
                        <b className="font-semibold text-foreground">{creditCharge}</b> — the
                        difference between the two prices above. Nothing further is added to
                        the hire-purchase price while the agreement runs; the only extra that
                        can arise is interest on a payment you make late.
                    </p>
                </Panel>
            )}

            {/*
              Clause 22.3 of the Terms in the words a person uses, because "and what that
              exclusion means" is the part the Schedule says was actually explained. The
              exclusion sentence is 22.3's own, and so are the two consequences: no ending the
              agreement over quality or condition, and no Primelane responsibility for the
              condition of the Vehicle or the cost of repairing it. The repair allocation is
              clause 27.4, WHICH §31 PARED TO ITS BARE FORM — the Driver's throughout the Hire
              Purchase Period, full stop. The written-agreement exception this panel used to
              offer left the clause with that pass, so it has left this panel; the six-month
              major-mechanical-fault warranty went earlier for the same reason. The disclosure
              may only claim what the documents claim.
            */}
            <Panel title="The vehicle is second-hand">
                <p>
                    Primelane is letting you a second-hand vehicle, and the agreement says so.
                </p>
                <p>
                    Any term about the quality or condition of the vehicle that the law would
                    otherwise imply is excluded by this agreement.
                </p>
                <p>
                    <b className="font-semibold text-foreground">What that means:</b> you
                    cannot end the agreement because of the quality or condition of the
                    vehicle, and Primelane is not responsible for the condition of the vehicle
                    or for the cost of repairing it. Maintaining and repairing the vehicle, and
                    paying for that work, are yours throughout the agreement.
                </p>
            </Panel>
        </>
    );
}
